/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, ember/require-tagless-components */
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
import { isArray } from '@ember/array';
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import BulkImportOutcome from 'linced-app/enums/bulk-import-outcome';
import BulkImportStatus from 'linced-app/enums/bulk-import-status';
import BulkImportType from 'linced-app/enums/bulk-import-type';
import ContactType from 'linced-app/enums/contact-type';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import config from 'linced-app/config/environment';

// NOTE: named ...Component to avoid colliding with the `BulkImportStatus`
// enum imported above.
export default class BulkImportStatusComponent extends Component {
  @service
  fetch;

  @service
  features;

  @service
  toaster;

  BulkImportOutcome = BulkImportOutcome;
  BulkImportStatus = BulkImportStatus;
  BulkImportType = BulkImportType;
  ContactType = ContactType;
  PersonType = PersonType;
  @tracked
  cancelDialogOpen = false;

  @computed('bulkImport.importType')
  get hasLearners() {
    switch (this.bulkImport.importType) {
      case BulkImportType.LEARNERS:
      case BulkImportType.MIXED:
        return true;

      default:
        return false;
    }
  }

  @computed('bulkImport.{status,log.[]}')
  get counts() {
    return this.bulkImport.log.reduce(
      (counts, logEntry) => {
        switch (logEntry.personType) {
          case PersonType.CAREGIVER:
          case PersonType.DOCTORS_AND_SPECIALISTS:
            switch (logEntry.outcome) {
              case BulkImportOutcome.CREATED:
                counts.numContactsCreated++;
                break;
              case BulkImportOutcome.UPDATED:
                counts.numContactsUpdated++;
                break;
              case BulkImportOutcome.UNCHANGED:
                counts.numContactsUnchanged++;
                break;
              case BulkImportOutcome.SKIPPED:
              case BulkImportOutcome.FAILED:
                counts.numContactsFailed++;
                break;
            }
            break;

          default:
            switch (logEntry.outcome) {
              case BulkImportOutcome.CREATED:
                counts.numPeopleCreated++;
                break;
              case BulkImportOutcome.UPDATED:
                counts.numPeopleUpdated++;
                break;
              case BulkImportOutcome.UNCHANGED:
                counts.numPeopleUnchanged++;
                break;
              case BulkImportOutcome.SKIPPED:
                counts.numPeopleSkipped++;
                break;
              case BulkImportOutcome.FAILED:
                counts.numPeopleFailed++;
                break;
            }
        }
        return counts;
      },
      {
        numPeopleCreated: 0,
        numPeopleUpdated: 0,
        numPeopleUnchanged: 0,
        numPeopleSkipped: 0,
        numPeopleFailed: 0,
        numContactsCreated: 0,
        numContactsUpdated: 0,
        numContactsUnchanged: 0,
        numContactsFailed: 0,
        numFinanceUpdates: this.get('bulkImport.financeProcessed') ?? 0,
        numWarnings: 0,
        numErrors: 0,
      },
    );
  }

  // primaryNames generates a map of row index to "primary" person's name.
  // This can be used to display the learner's name on associated caregivers'
  // rows, as there's no direct way to get the learner's name from a caregiver.
  @computed('bulkImport.log.[]')
  get primaryNames() {
    if (!isArray(this.bulkImport.log)) {
      return {};
    }
    return this.bulkImport.log.reduce((names, logEntry) => {
      if (
        logEntry.personType !== PersonType.CAREGIVER &&
        logEntry.personType !== PersonType.DOCTORS_AND_SPECIALISTS
      ) {
        names[logEntry.row] = logEntry.name;
      }
      return names;
    }, {});
  }

  @action
  toggleCancelDialog() {
    this.toggleProperty('cancelDialogOpen');
  }

  @action
  cancelImport() {
    let url = `${config.APP.apiHost}/people/${config.APP.apiPath}/bulkimports/${this.bulkImport.id}/:cancel`;
    return this.fetch
      .post(url)
      .then(() => {
        this.toaster.success('Bulk import cancellation requested.');
      })
      .catch(() => {
        this.toaster.error('Bulk import cancellation failed.');
      })
      .finally(() => {
        this.cancelDialogOpen = false;
      });
  }
}
